export interface EncryptedValue {
  iv: number[];
  ciphertext: number[];
}

export interface VaultMetadata {
  scope: string;
  version: 1;
  keyId: string;
  expiresAt: string;
  salt: number[];
  wrappedKey: EncryptedValue;
}

export interface VaultRecord {
  id: string;
  scope: string;
  value: EncryptedValue;
  // Plaintext on purpose: marks a synced operation, so an expired vault can be pruned without its passphrase.
  settled?: boolean;
}

const DATABASE = 'meridian-event-vault';

async function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore('vaults', { keyPath: 'scope' });
      request.result.createObjectStore('records', { keyPath: 'id' }).createIndex('scope', 'scope');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error('Offline storage could not be opened. Your activity has not been saved.'));
    request.onblocked = () => reject(new Error('Close other Meridian tabs to update offline storage.'));
  });
}

async function transaction<T>(stores: string[], mode: IDBTransactionMode, run: (tx: IDBTransaction, result: (value: T) => void) => void): Promise<T> {
  const db = await database();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(stores, mode);
      let value: T;
      tx.oncomplete = () => resolve(value);
      tx.onabort = () => reject(tx.error ?? new Error('Offline storage failed. Your activity has not been saved.'));
      tx.onerror = () => undefined;
      run(tx, (result) => { value = result; });
    });
  } finally {
    db.close();
  }
}

export const vaultStorage = {
  allMetadata: () => transaction<VaultMetadata[]>(['vaults'], 'readonly', (tx, done) => {
    tx.objectStore('vaults').getAll().onsuccess = event => done((event.target as IDBRequest<VaultMetadata[]>).result);
  }),
  metadata: (scope: string) => transaction<VaultMetadata | undefined>(['vaults'], 'readonly', (tx, done) => {
    tx.objectStore('vaults').get(scope).onsuccess = event => done((event.target as IDBRequest<VaultMetadata | undefined>).result);
  }),
  create: (metadata: VaultMetadata, snapshot: VaultRecord) => transaction<void>(['vaults', 'records'], 'readwrite', tx => {
    tx.objectStore('vaults').add(metadata);
    tx.objectStore('records').add(snapshot);
  }),
  restore: (metadata: VaultMetadata, records: VaultRecord[]) => transaction<void>(['vaults', 'records'], 'readwrite', tx => {
    tx.objectStore('vaults').add(metadata);
    for (const record of records) tx.objectStore('records').add(record);
  }),
  records: (scope: string) => transaction<VaultRecord[]>(['records'], 'readonly', (tx, done) => {
    tx.objectStore('records').index('scope').getAll(scope).onsuccess = event => done((event.target as IDBRequest<VaultRecord[]>).result);
  }),
  get: (id: string) => transaction<VaultRecord | undefined>(['records'], 'readonly', (tx, done) => {
    tx.objectStore('records').get(id).onsuccess = event => done((event.target as IDBRequest<VaultRecord | undefined>).result);
  }),
  put: (record: VaultRecord, insertOnly = false, expectedKeyId?: string) => transaction<void>(['vaults', 'records'], 'readwrite', tx => {
    const write = () => {
      const store = tx.objectStore('records');
      if (insertOnly) store.add(record); else store.put(record);
    };
    if (!expectedKeyId) { write(); return; }
    const request = tx.objectStore('vaults').get(record.scope);
    request.onsuccess = () => {
      if ((request.result as VaultMetadata | undefined)?.keyId !== expectedKeyId) { tx.abort(); return; }
      write();
    };
  }),
  remove: (ids: string[]) => transaction<void>(['records'], 'readwrite', tx => {
    for (const id of ids) tx.objectStore('records').delete(id);
  }),
  clear: (scope: string) => transaction<void>(['vaults', 'records'], 'readwrite', tx => {
    tx.objectStore('vaults').delete(scope);
    tx.objectStore('records').index('scope').openCursor(scope).onsuccess = event => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
      if (cursor) { cursor.delete(); cursor.continue(); }
    };
  }),
};
